import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { AxiosError } from 'axios'
import { SortSliceEmployeeAgreementHistory } from './HandbookSettings/empHandbookSliceHelper'
import { ApiLoadingState } from '../../middleware/api/apiList'
import employeeHandbookApi from '../../middleware/api/EmployeeHandbook/employeeHandbook'
import { AppDispatch, RootState } from '../../stateStore'
import { LoadingState, ValidationError } from '../../types/commonTypes'
import {
  AddPolicyPayload,
  EmployeeAcceptedPolicy,
  EmployeeHandbooksState,
  EmployeePolicyAgreementType,
  Handbook,
} from '../../types/EmployeeHandbook/employeeHandbookTypes'

const getHandbooks = createAsyncThunk<
  Handbook[] | undefined,
  string | undefined,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('employeeHandbook/getHandbooks', async (_, thunkApi) => {
  try {
    return await employeeHandbookApi.getHandbooks()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getPublishedPolicy = createAsyncThunk<
  Handbook[] | undefined,
  string | undefined,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('employeeHandbook/getPublishedPolicy', async (_, thunkApi) => {
  try {
    return await employeeHandbookApi.getPublishedPolicy()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const getLoggedinEmployeeHandbookData = createAsyncThunk<
  EmployeePolicyAgreementType[] | undefined,
  void,
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>('employeeHandbook/getLoggedinEmployeeHandbookData', async (_, thunkApi) => {
  try {
    return await employeeHandbookApi.getLoggedinEmployeeHandbookData()
  } catch (error) {
    const err = error as AxiosError
    return thunkApi.rejectWithValue(err.response?.status as ValidationError)
  }
})

const addPolicyStatus = createAsyncThunk<
  void, // returned type
  AddPolicyPayload, // payload type
  {
    dispatch: AppDispatch
    state: RootState
    rejectValue: ValidationError
  }
>(
  'employeeHandbook/addPolicyStatus',
  async (obj: AddPolicyPayload, thunkApi) => {
    try {
      return await employeeHandbookApi.addPolicyStatus(obj)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const getEmployeeAcceptedPolicies = createAsyncThunk(
  'employeeHandbook/getEmployeeAcceptedPolicies',
  async (_, thunkApi) => {
    try {
      return await employeeHandbookApi.getEmployeeAcceptedPolicy()
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)
const savePolicy = createAsyncThunk(
  'employeeHandbook/savePolicy',
  async (props: EmployeeAcceptedPolicy, thunkApi) => {
    try {
      return await employeeHandbookApi.savePolicy(props)
    } catch (error) {
      const err = error as AxiosError
      return thunkApi.rejectWithValue(err.response?.status as ValidationError)
    }
  },
)

const initialHandbooksState: EmployeeHandbooksState = {
  handbooksList: [],
  publishedPolicyList: [] as Handbook[],
  updatedNewPublish: [] as Handbook[],
  employeePolicyAgreementHistory: [] as EmployeePolicyAgreementType[],
  isLoading: ApiLoadingState.idle,
  isLoadingPublishedpolicies: ApiLoadingState.idle,
  isLoadingAcceptedpolicies: ApiLoadingState.idle,
  error: null,
  prePopupAgreeFlag: false,
  signOffPopupAgreeFlag: false,
  newPolicyUpdateFromHSettingsFlag: false,
  policyDeleteFromHSettingsFlag: false,
  originBack: '',
  filterOptions: {
    isMandatory: '',
    isPublished: '',
    country: '',
    search: '',
  },
  isRender: true,
  employeeAcceptedPolicies: [] as EmployeeAcceptedPolicy[],
  fromSearch: false,
}

const employeeHandbookSlice = createSlice({
  name: 'handbooks',
  initialState: initialHandbooksState,
  reducers: {
    //! Deprecated - was not able to use the selector for this since redux always resets on route change
    setUpdatedNewPublish: (state, action) => {
      state.updatedNewPublish = action.payload
    },
    setPrePopupAgreeFlag: (state, action) => {
      state.prePopupAgreeFlag = action.payload
    },
    setSignOffPopupAgreeFlag: (state, action) => {
      state.signOffPopupAgreeFlag = action.payload
    },
    setPolicyUpdateFromHSettingsFlag: (state, action) => {
      state.newPolicyUpdateFromHSettingsFlag = action.payload
    },
    setPolicyDeleteFromHSettingsFlag: (state, action) => {
      state.policyDeleteFromHSettingsFlag = action.payload
    },
    setOriginBack: (state, action) => {
      state.originBack = action.payload
    },
    setFilterOptions: (state, action) => {
      state.filterOptions = action.payload
    },
    clearFilterOptions: (state) => {
      state.filterOptions = {
        isMandatory: '',
        isPublished: '',
        country: '',
        search: '',
      }
      state.fromSearch = false
    },
    setIsRender: (state, action) => {
      state.isRender = action.payload
    },
    setFromSearch: (state, action) => {
      state.fromSearch = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEmployeeAcceptedPolicies.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
        state.isLoadingAcceptedpolicies = ApiLoadingState.loading
      })
      .addCase(getEmployeeAcceptedPolicies.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.isLoadingAcceptedpolicies = ApiLoadingState.succeeded
        if (action.payload) {
          state.employeeAcceptedPolicies = (
            action.payload as EmployeeAcceptedPolicy[]
          ).map((policy) => {
            return {
              ...policy,
              pageName: policy.pageName.trimEnd(),
            }
          })
        } else {
          state.employeeAcceptedPolicies = [] as EmployeeAcceptedPolicy[]
        }
      })
      .addCase(getEmployeeAcceptedPolicies.rejected, (state) => {
        state.isLoading = ApiLoadingState.failed
        state.isLoadingAcceptedpolicies = ApiLoadingState.failed
      })
      .addCase(getHandbooks.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getHandbooks.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded
        state.handbooksList = action.payload as Handbook[]
      })
      .addCase(getHandbooks.rejected, (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.error = action.payload as ValidationError
      })
      // getPublishedPolicy
      .addCase(getPublishedPolicy.pending, (state) => {
        state.isLoadingPublishedpolicies = ApiLoadingState.loading
      })
      .addCase(getPublishedPolicy.fulfilled, (state, action) => {
        state.isLoadingPublishedpolicies = ApiLoadingState.succeeded
        if (action.payload) {
          state.publishedPolicyList = (action.payload as Handbook[])
            .slice()
            .sort((a, b) => {
              // Trim the pageName property
              a.pageName = a.pageName.trimEnd()
              b.pageName = b.pageName.trimEnd()
              // Sort by displayOrder
              return a.displayOrder - b.displayOrder
            })
        } else {
          state.publishedPolicyList = [] as Handbook[]
        }
      })
      .addCase(getPublishedPolicy.rejected, (state, action) => {
        state.isLoadingPublishedpolicies = ApiLoadingState.failed
        state.error = action.payload as ValidationError
      })
      // getLoggedinEmployeeHandbookData
      .addCase(getLoggedinEmployeeHandbookData.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(getLoggedinEmployeeHandbookData.fulfilled, (state, action) => {
        state.isLoading = ApiLoadingState.succeeded

        const sortedPayload = SortSliceEmployeeAgreementHistory(
          action.payload as EmployeePolicyAgreementType[],
        )
        state.employeePolicyAgreementHistory = sortedPayload
      })
      .addCase(getLoggedinEmployeeHandbookData.rejected, (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.error = action.payload as ValidationError
      })
      // addPolicyStatus
      .addCase(addPolicyStatus.pending, (state) => {
        state.isLoading = ApiLoadingState.loading
      })
      .addCase(addPolicyStatus.fulfilled, (state) => {
        state.isLoading = ApiLoadingState.succeeded
      })
      .addCase(addPolicyStatus.rejected, (state, action) => {
        state.isLoading = ApiLoadingState.failed
        state.error = action.payload as ValidationError
      })
  },
})

const isLoading = (state: RootState): LoadingState => {
  return state.employeeHandbook.isLoading
}

const isLoadingAcceptedpolicies = (state: RootState): LoadingState => {
  return state.employeeHandbook.isLoadingAcceptedpolicies
}
const isLoadingPublishedpolicies = (state: RootState): LoadingState => {
  return state.employeeHandbook.isLoadingPublishedpolicies
}

const handbookData = (state: RootState): Handbook[] => {
  return state.employeeHandbook.handbooksList
}

const publishedPolicyList = (state: RootState): Handbook[] => {
  return state.employeeHandbook.publishedPolicyList
}

const employeePolicyAgreementHistory = (
  state: RootState,
): EmployeePolicyAgreementType[] => {
  return state.employeeHandbook.employeePolicyAgreementHistory
}
/**
 * ! Deprecated - was not able to use the selector for this since redux always resets on route change
 */
const getUpdatedNewPublish = (state: RootState): Handbook[] => {
  return state.employeeHandbook.updatedNewPublish
}

const getPrePopupAgreeFlag = (state: RootState): boolean => {
  return state.employeeHandbook.prePopupAgreeFlag
}
const getSignOffPopupAgreeFlag = (state: RootState): boolean => {
  return state.employeeHandbook.signOffPopupAgreeFlag
}
const getPolicyUpdateFromHSettingsFlag = (state: RootState): boolean => {
  return state.employeeHandbook.newPolicyUpdateFromHSettingsFlag
}
const getPolicyDeleteFromHSettingsFlag = (state: RootState): boolean => {
  return state.employeeHandbook.policyDeleteFromHSettingsFlag
}
const getOriginBack = (state: RootState): string => {
  return state.employeeHandbook.originBack
}
const getIsRender = (state: RootState): boolean => {
  return state.employeeHandbook.isRender
}
const getFromSearch = (state: RootState): boolean => {
  return state.employeeHandbook.fromSearch
}

const getFilterOptions = (
  state: RootState,
): {
  isMandatory: string | boolean
  isPublished: string | boolean
  country: string
  search: string
} => {
  return state.employeeHandbook.filterOptions
}
const getEmpAcceptedPolicies = (state: RootState): EmployeeAcceptedPolicy[] => {
  return state.employeeHandbook.employeeAcceptedPolicies
}

const employeeHandbookThunk = {
  getHandbooks,
  getPublishedPolicy,
  getLoggedinEmployeeHandbookData,
  addPolicyStatus,
  getEmployeeAcceptedPolicies,
  savePolicy,
}

const employeeHandbookSelectors = {
  isLoading,
  isLoadingPublishedpolicies,
  isLoadingAcceptedpolicies,
  handbookData,
  publishedPolicyList,
  employeePolicyAgreementHistory,
  getUpdatedNewPublish,
  getPrePopupAgreeFlag,
  getSignOffPopupAgreeFlag,
  getPolicyUpdateFromHSettingsFlag,
  getPolicyDeleteFromHSettingsFlag,
  getOriginBack,
  getFilterOptions,
  getIsRender,
  getEmpAcceptedPolicies,
  getFromSearch,
}

export const EmployeeHandbookService = {
  ...employeeHandbookThunk,
  actions: employeeHandbookSlice.actions,
  selectors: employeeHandbookSelectors,
}

export default employeeHandbookSlice.reducer
